import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { API_URL } from '../config';

// ── Pomocné funkce ────────────────────────────────────────────────────────────
const fmt = (n) => String(n).padStart(2, '0');
const localDateStr = (d) => d.toLocaleDateString('en-CA'); // YYYY-MM-DD

const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return '–';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${fmt(m)}m` : `${m}m`;
};

const formatTimeIso = (isoString) => {
  if (!isoString) return '–';
  const ts = new Date(isoString).getTime();
  const pd = new Date(ts + 2 * 3600 * 1000); // Praha ~ UTC+2
  return `${fmt(pd.getUTCHours())}:${fmt(pd.getUTCMinutes())}`;
};

const monthNames = ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'];
const monthShort = ['led', 'úno', 'bře', 'dub', 'kvě', 'čvn', 'čvc', 'srp', 'zář', 'říj', 'lis', 'pro'];

// ── Hodinový graf pracovní doby (7–21) ────────────────────────────────────────
function HourlyWorkChart({ entry, onBarClick }) {
  const HOUR_START = 7;
  const HOUR_END = 21;
  const HOUR_COUNT = HOUR_END - HOUR_START + 1;
  const buckets = Array(HOUR_COUNT).fill(0);
  const timeline = entry?.timeline || [];
  timeline.forEach((minOfDay) => {
    const h = Math.floor(minOfDay / 60);
    if (h >= HOUR_START && h <= HOUR_END) buckets[h - HOUR_START]++;
  });
  const maxBucket = Math.max(...buckets, 1);
  const startHour = timeline.length ? Math.max(HOUR_START, Math.floor(Math.min(...timeline) / 60)) : HOUR_START;
  const endHour = timeline.length ? Math.min(HOUR_END, Math.floor(Math.max(...timeline) / 60) + 1) : HOUR_END;

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3 text-xs">
        <span className="bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 px-2.5 py-1 rounded-full">
          🕐 Začátek: {formatTimeIso(entry?.firstActivity)}
        </span>
        <span className="bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 px-2.5 py-1 rounded-full">
          🕐 Konec: {formatTimeIso(entry?.lastActivity)}
        </span>
        <span className="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 px-2.5 py-1 rounded-full">
          ⏱ Odpracováno: {formatDuration(entry?.workedMinutes)}
        </span>
        <span className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2.5 py-1 rounded-full">
          📝 Aktivit: {entry?.activityCount || 0}
        </span>
      </div>
      <div className="flex items-end gap-[3px] h-28 border-b border-gray-200 dark:border-gray-600 pb-1">
        {buckets.map((count, i) => {
          const h = HOUR_START + i;
          const inRange = h >= startHour && h <= endHour;
          const heightPct = count > 0 ? Math.max(8, Math.round((count / maxBucket) * 100)) : 0;
          const clickable = count > 0 && typeof onBarClick === 'function';
          return (
            <div
              key={h}
              className={`flex-1 flex flex-col items-center justify-end h-full ${clickable ? 'cursor-pointer group' : ''}`}
              title={`${fmt(h)}:00 – ${count} aktivit${clickable ? ' (klikni pro seznam)' : ''}`}
              onClick={clickable ? () => onBarClick(h) : undefined}
            >
              <div
                className={`w-full rounded-t transition-all ${
                  count > 0 ? (inRange ? 'bg-blue-500 dark:bg-blue-400 group-hover:bg-blue-600 dark:group-hover:bg-blue-300' : 'bg-gray-300 dark:bg-gray-600 group-hover:bg-gray-400') : ''
                }`}
                style={{ height: count > 0 ? `${heightPct}%` : '2px', minHeight: count > 0 ? '4px' : undefined }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-[3px] mt-1">
        {buckets.map((_, i) => (
          <div key={i} className="flex-1 text-center text-[9px] text-gray-400 dark:text-gray-500">
            {fmt(HOUR_START + i)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Sloupcový rozpad odpracovaného času (týdny / měsíce) ──────────────────────
function WorkBreakdownChart({ bars }) {
  if (!bars || bars.length === 0) {
    return <div className="text-sm text-gray-400 dark:text-gray-500 py-6 text-center">Žádná data.</div>;
  }
  const maxMin = Math.max(...bars.map((b) => b.minutes), 1);
  return (
    <div className="flex items-end gap-2 h-32 pt-6 overflow-x-auto">
      {bars.map((b, i) => {
        const heightPct = b.minutes > 0 ? Math.max(6, Math.round((b.minutes / maxMin) * 100)) : 0;
        return (
          <div key={i} className="flex-1 flex flex-col items-center justify-end h-full min-w-[28px]" title={`${b.label}: ${formatDuration(b.minutes)}${b.sub ? ` · ${b.sub}` : ''}`}>
            <span className="text-[9px] text-gray-500 dark:text-gray-400 mb-0.5 whitespace-nowrap">{formatDuration(b.minutes)}</span>
            <div
              className="w-full rounded-t bg-blue-500 dark:bg-blue-400 transition-all"
              style={{ height: b.minutes > 0 ? `${heightPct}%` : '2px', minHeight: b.minutes > 0 ? '4px' : '2px' }}
            />
            <span className="text-[9px] text-gray-500 dark:text-gray-400 mt-1 text-center leading-tight whitespace-nowrap">{b.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Spojnicový graf vývoje počtu aktivit (posledních 14 pracovních dnů) ───────
function ActivityTrendChart({ points }) {
  if (!points || points.length === 0) {
    return <div className="text-sm text-gray-400 dark:text-gray-500 py-8 text-center">Žádná data.</div>;
  }
  const W = 320;
  const H = 150;
  const padL = 24;
  const padR = 8;
  const padT = 12;
  const padB = 22;
  const maxCount = Math.max(...points.map((p) => p.count), 1);
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const x = (i) => padL + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
  const y = (v) => padT + innerH - (v / maxCount) * innerH;

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.count).toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${x(points.length - 1).toFixed(1)},${(padT + innerH).toFixed(1)} L${x(0).toFixed(1)},${(padT + innerH).toFixed(1)} Z`;

  const half = Math.floor(points.length / 2);
  const avg = (arr) => (arr.length ? arr.reduce((s, p) => s + p.count, 0) / arr.length : 0);
  const firstAvg = avg(points.slice(0, half));
  const secondAvg = avg(points.slice(half));
  const diff = secondAvg - firstAvg;
  const trendLabel = diff > 0.5 ? '↗ roste' : diff < -0.5 ? '↘ klesá' : '→ stabilní';
  const trendColor = diff > 0.5 ? 'text-green-600 dark:text-green-400' : diff < -0.5 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400';

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-500 dark:text-gray-400">Ø {avg(points).toFixed(1)} aktivit/den</span>
        <span className={`text-xs font-semibold ${trendColor}`}>{trendLabel}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        {[0, 0.5, 1].map((f) => {
          const val = Math.round(maxCount * f);
          const yy = y(val);
          return (
            <g key={f}>
              <line x1={padL} y1={yy} x2={W - padR} y2={yy} className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="1" />
              <text x={padL - 4} y={yy + 3} textAnchor="end" className="fill-gray-400 dark:fill-gray-500" fontSize="9">{val}</text>
            </g>
          );
        })}
        <path d={areaPath} className="fill-blue-500/10 dark:fill-blue-400/10" />
        <path d={linePath} fill="none" className="stroke-blue-500 dark:stroke-blue-400" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={x(i)} cy={y(p.count)} r="2.5" className="fill-blue-600 dark:fill-blue-300">
              <title>{`${p.date.toLocaleDateString('cs-CZ', { weekday: 'short', day: 'numeric', month: 'numeric' })}: ${p.count} aktivit`}</title>
            </circle>
            <text x={x(i)} y={H - 8} textAnchor="middle" className="fill-gray-400 dark:fill-gray-500" fontSize="8">
              {p.date.getDate()}/{p.date.getMonth() + 1}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

// ── Sloupcový graf vygenerovaných + uzavřených leadů po měsících ──────────────
function LeadsMonthlyChart({ bars, onBarClick }) {
  if (!bars || bars.length === 0) {
    return <div className="text-sm text-gray-400 dark:text-gray-500 py-6 text-center">Žádná data.</div>;
  }
  const maxVal = Math.max(...bars.map((b) => b.generated), 1);
  return (
    <div className="flex items-end gap-2 h-32 pt-6">
      {bars.map((b, i) => {
        const genPct = b.generated > 0 ? Math.max(6, Math.round((b.generated / maxVal) * 100)) : 0;
        const closedPct = b.generated > 0 ? Math.round((b.closed / maxVal) * 100) : 0;
        const clickable = b.generated > 0 && typeof onBarClick === 'function';
        return (
          <div
            key={i}
            className={`flex-1 flex flex-col items-center justify-end h-full min-w-0 ${clickable ? 'cursor-pointer group' : ''}`}
            title={`${b.label}: ${b.generated} leadů, ${b.closed} uzavřeno (${b.closeRate} %)`}
            onClick={clickable ? () => onBarClick(i) : undefined}
          >
            <span className="text-[9px] text-gray-500 dark:text-gray-400 mb-0.5 whitespace-nowrap">{b.closeRate}%</span>
            <div className="w-full relative flex flex-col justify-end" style={{ height: `${genPct}%`, minHeight: b.generated > 0 ? '4px' : '2px' }}>
              {/* Vygenerované (podklad) */}
              <div className="absolute inset-0 rounded-t bg-gray-300 dark:bg-gray-600" />
              {/* Uzavřené (překryv) */}
              <div
                className="absolute bottom-0 left-0 right-0 rounded-t bg-green-500 dark:bg-green-400 transition-all"
                style={{ height: b.generated > 0 ? `${Math.round((b.closed / b.generated) * 100)}%` : '0%' }}
              />
            </div>
            <span className="text-[9px] text-gray-500 dark:text-gray-400 mt-1 text-center leading-tight whitespace-nowrap">{b.label}</span>
            <span className="text-[9px] text-gray-400 dark:text-gray-500 leading-tight whitespace-nowrap">{b.closed}/{b.generated}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Malý statistický blok ─────────────────────────────────────────────────────
function StatCard({ label, value, sub, color = 'blue', onClick }) {
  const colors = {
    blue: 'border-blue-500',
    green: 'border-green-500',
    orange: 'border-orange-500',
    purple: 'border-purple-500',
    cyan: 'border-cyan-500',
    pink: 'border-pink-500',
    red: 'border-red-500',
  };
  const clickable = typeof onClick === 'function';
  return (
    <div
      onClick={onClick}
      className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 ${colors[color] || colors.blue} ${clickable ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`}
    >
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">{value}</p>
      {sub && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

// ── Poměrový bar (dvě hodnoty) ────────────────────────────────────────────────
function RatioBar({ title, aLabel, aCount, aColor, aOnClick, bLabel, bCount, bColor, bOnClick }) {
  const total = aCount + bCount;
  const aPct = total > 0 ? Math.round((aCount / total) * 100) : 0;
  const bPct = total > 0 ? Math.round((bCount / total) * 100) : 0;
  const aClickable = aCount > 0 && typeof aOnClick === 'function';
  const bClickable = bCount > 0 && typeof bOnClick === 'function';
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">{title}</p>
      <div
        className={`mb-2 ${aClickable ? 'cursor-pointer rounded-lg -mx-1 px-1 hover:bg-gray-50 dark:hover:bg-gray-700/50' : ''}`}
        onClick={aClickable ? aOnClick : undefined}
      >
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-gray-600 dark:text-gray-400">{aLabel}</span>
          <span className={`text-xs font-semibold ${aColor.text}`}>{aCount} ({aPct}%)</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div className={`${aColor.bg} h-2 rounded-full transition-all`} style={{ width: `${aPct}%` }} />
        </div>
      </div>
      <div
        className={`${bClickable ? 'cursor-pointer rounded-lg -mx-1 px-1 hover:bg-gray-50 dark:hover:bg-gray-700/50' : ''}`}
        onClick={bClickable ? bOnClick : undefined}
      >
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-gray-600 dark:text-gray-400">{bLabel}</span>
          <span className={`text-xs font-semibold ${bColor.text}`}>{bCount} ({bPct}%)</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div className={`${bColor.bg} h-2 rounded-full transition-all`} style={{ width: `${bPct}%` }} />
        </div>
      </div>
    </div>
  );
}

// ── Modální seznam zákazníků (klik → otevřít zákazníka) ───────────────────────
function ListModal({ title, items, onClose, onOpen }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-bold text-gray-800 dark:text-white">{title} ({items.length})</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none">×</button>
        </div>
        <div className="overflow-y-auto p-2">
          {items.length > 0 ? items.map((it, i) => (
            <div
              key={i}
              onClick={() => it.id && onOpen(it.id)}
              className={`flex items-center justify-between px-3 py-2.5 rounded-lg ${it.id ? 'cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/20' : ''} transition-colors`}
              title={it.id ? 'Otevřít zákazníka' : ''}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{it.name}</p>
                {it.sub && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{it.sub}</p>}
              </div>
              {it.id && <span className="text-purple-600 dark:text-purple-400 text-sm whitespace-nowrap ml-3">otevřít →</span>}
            </div>
          )) : (
            <div className="text-sm text-gray-400 dark:text-gray-500 py-6 text-center">Žádné položky.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CallcentrumReports({ currentUserId, userRole, users = [], onSwitchUser }) {
  // Pouze uživatelé s rolí callcentrum
  const ccUsers = useMemo(() => {
    return users
      .filter((u) => u.role === 'callcentrum' || u.role === 'callcenter')
      .map((u) => ({ _id: u._id, name: u.name || u.username || 'Neznámý' }))
      .sort((a, b) => a.name.localeCompare(b.name, 'cs'));
  }, [users]);

  const isAdmin = userRole === 'admin' || userRole === 'superadmin';

  const [selectedUser, setSelectedUser] = useState(ccUsers[0]?._id || '');
  const [selectedDay, setSelectedDay] = useState(localDateStr(new Date()));

  const [workEntry, setWorkEntry] = useState(null);
  const [workRange, setWorkRange] = useState([]);
  const [dayActivities, setDayActivities] = useState([]);
  const [report, setReport] = useState(null); // { monthly, leads, totals }
  const [loading, setLoading] = useState(false);
  const [listModal, setListModal] = useState(null);

  // Historie denního přidělování – přepínání měsíců
  const [assignMonth, setAssignMonth] = useState(new Date().getMonth() + 1);
  const [assignYear, setAssignYear] = useState(new Date().getFullYear());
  const [assignDays, setAssignDays] = useState(null); // days[] pro vybraného uživatele
  const [assignLoading, setAssignLoading] = useState(false);

  useEffect(() => {
    if (!selectedUser && ccUsers[0]) setSelectedUser(ccUsers[0]._id);
  }, [ccUsers, selectedUser]);

  const userName = ccUsers.find((s) => s._id === selectedUser)?.name || '';

  // Načtení pracovní doby + aktivit pro vybraný den
  const loadDayData = useCallback(async () => {
    if (!selectedUser || !selectedDay) return;
    try {
      const [wtRes, actRes] = await Promise.all([
        fetch(`${API_URL}/work-time-stats?from=${selectedDay}&to=${selectedDay}&userId=${currentUserId}`),
        fetch(`${API_URL}/activities?userId=${currentUserId}&viewUserId=${selectedUser}&days=all`),
      ]);
      const wtData = wtRes.ok ? await wtRes.json() : [];
      setWorkEntry(wtData.find((e) => String(e.userId) === String(selectedUser)) || null);

      const actData = actRes.ok ? await actRes.json() : { activities: [] };
      const acts = (actData.activities || []).filter((a) => {
        if (!a.createdAt) return false;
        return localDateStr(new Date(a.createdAt)) === selectedDay;
      });
      acts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setDayActivities(acts);
    } catch (e) {
      console.error('CallcentrumReports loadDayData error', e);
      setWorkEntry(null);
      setDayActivities([]);
    }
  }, [selectedUser, selectedDay, currentUserId]);

  // Načtení souhrnného reportu (leady, uzavřenost, reklamovatelnost, PO/FO)
  const loadReport = useCallback(async () => {
    if (!selectedUser) { setReport(null); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/callcenter-user-report?filterUserId=${selectedUser}&months=6&userId=${currentUserId}`);
      setReport(res.ok ? await res.json() : null);
    } catch (e) {
      console.error('CallcentrumReports loadReport error', e);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [selectedUser, currentUserId]);

  // Delší historie pracovní doby (12 měsíců) pro rozpad + trend
  const loadWorkRange = useCallback(async () => {
    if (!selectedUser) { setWorkRange([]); return; }
    try {
      const today = new Date();
      const from = new Date(today);
      from.setMonth(from.getMonth() - 12);
      from.setDate(1);
      const res = await fetch(`${API_URL}/work-time-stats?from=${localDateStr(from)}&to=${localDateStr(today)}&userId=${currentUserId}`);
      const data = res.ok ? await res.json() : [];
      setWorkRange((data || []).filter((e) => String(e.userId) === String(selectedUser)));
    } catch (e) {
      console.error('CallcentrumReports loadWorkRange error', e);
      setWorkRange([]);
    }
  }, [selectedUser, currentUserId]);

  // Denní historie přidělování kontaktů pro vybraný měsíc
  const loadAssignmentDaily = useCallback(async () => {
    if (!selectedUser) { setAssignDays(null); return; }
    setAssignLoading(true);
    try {
      const res = await fetch(`${API_URL}/assignment-history-daily?month=${assignMonth}&year=${assignYear}&userId=${currentUserId}`);
      const data = res.ok ? await res.json() : [];
      const mine = (data || []).find((u) => String(u.userId) === String(selectedUser));
      setAssignDays(mine?.days || []);
    } catch (e) {
      console.error('CallcentrumReports loadAssignmentDaily error', e);
      setAssignDays([]);
    } finally {
      setAssignLoading(false);
    }
  }, [selectedUser, assignMonth, assignYear, currentUserId]);

  useEffect(() => { loadDayData(); }, [loadDayData]);
  useEffect(() => { loadReport(); }, [loadReport]);
  useEffect(() => { loadWorkRange(); }, [loadWorkRange]);
  useEffect(() => { loadAssignmentDaily(); }, [loadAssignmentDaily]);

  // ── Odvozené statistiky ─────────────────────────────────────────────────────
  const totals = report?.totals || { generated: 0, closed: 0, closeRate: 0, complaints: 0, complaintRate: 0, poCount: 0, foCount: 0 };
  const leads = report?.leads || [];
  const monthly = report?.monthly || [];

  const closedLeads = useMemo(() => leads.filter((l) => l.closed), [leads]);
  const complainedLeads = useMemo(() => leads.filter((l) => l.complained), [leads]);
  const poLeads = useMemo(() => leads.filter((l) => l.personType === 'Právnická osoba'), [leads]);
  const foLeads = useMemo(() => leads.filter((l) => l.personType === 'Fyzická osoba'), [leads]);

  // Mapa stavu firmy podle companyId (pro sloupec "Stav" v aktivitách)
  const companyStatusMap = useMemo(() => {
    const m = {};
    leads.forEach((l) => { if (l.companyId) m[String(l.companyId)] = l.status; });
    return m;
  }, [leads]);

  // Rozpad odpracovaného času po týdnech (posledních 12 týdnů)
  const weeklyWork = useMemo(() => {
    const byWeek = {};
    workRange.forEach((e) => {
      if (!e.dateKey) return;
      const d = new Date(e.dateKey + 'T12:00:00');
      const monday = new Date(d);
      const dow = (monday.getDay() + 6) % 7;
      monday.setDate(monday.getDate() - dow);
      const key = localDateStr(monday);
      if (!byWeek[key]) byWeek[key] = { key, minutes: 0, days: 0, monday };
      byWeek[key].minutes += e.workedMinutes || 0;
      byWeek[key].days += 1;
    });
    return Object.values(byWeek).sort((a, b) => a.monday - b.monday).slice(-12);
  }, [workRange]);

  // Vývoj počtu aktivit za posledních 14 pracovních dnů (po–pá)
  const activityTrend = useMemo(() => {
    const countByDate = {};
    workRange.forEach((e) => {
      if (e.dateKey) countByDate[e.dateKey] = e.activityCount || 0;
    });
    const days = [];
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    while (days.length < 14) {
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) {
        const key = localDateStr(d);
        days.push({ key, date: new Date(d), count: countByDate[key] || 0 });
      }
      d.setDate(d.getDate() - 1);
    }
    return days.reverse();
  }, [workRange]);

  // Celkem přiděleno v měsíci + rozpad po dnech
  const assignTotal = useMemo(() => (assignDays || []).reduce((s, d) => s + (d.total || 0), 0), [assignDays]);

  const shiftDay = (delta) => {
    const d = new Date(selectedDay);
    d.setDate(d.getDate() + delta);
    setSelectedDay(localDateStr(d));
  };

  const shiftAssignMonth = (delta) => {
    let m = assignMonth + delta;
    let y = assignYear;
    if (m < 1) { m = 12; y -= 1; }
    else if (m > 12) { m = 1; y += 1; }
    setAssignMonth(m);
    setAssignYear(y);
  };

  const handleOpenCompany = (companyId) => {
    if (!companyId) return;
    if (onSwitchUser) onSwitchUser(selectedUser, userName, companyId);
  };

  const openList = (title, arr, subFn) => {
    const items = (arr || []).map((c) => ({
      id: c.companyId ? String(c.companyId) : null,
      name: c.companyName || c.name || '—',
      sub: subFn ? subFn(c) : '',
    }));
    setListModal({ title, items });
  };

  const openListItem = (companyId) => {
    setListModal(null);
    handleOpenCompany(companyId);
  };

  const activityHour = (a) => {
    if (!a.createdAt) return null;
    const pd = new Date(new Date(a.createdAt).getTime() + 2 * 3600 * 1000);
    return pd.getUTCHours();
  };

  const openHourList = (hour) => {
    const acts = dayActivities.filter((a) => activityHour(a) === hour);
    setListModal({
      title: `Aktivity v ${fmt(hour)}:00`,
      items: acts.map((a) => ({
        id: a.companyId ? String(a.companyId) : null,
        name: a.companyName || '—',
        sub: `${formatTimeIso(a.createdAt)} · ${a.text || ''}`.trim(),
      })),
    });
  };

  const formatDayDate = (dateStr) => {
    if (!dateStr) return '–';
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('cs-CZ', { weekday: 'short', day: 'numeric', month: 'numeric' });
  };

  if (ccUsers.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500 dark:text-gray-400">
        Nejsou k dispozici žádní uživatelé s rolí callcentrum.
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500 dark:text-gray-400">
        Tento report je dostupný pouze pro administrátory.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Ovládací lišta: výběr callcentrum uživatele + dne */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex flex-wrap items-center gap-3 sticky top-0 z-20">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">📞 Callcentrum:</span>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:border-purple-500 focus:outline-none"
          >
            {ccUsers.map((s) => (
              <option key={s._id} value={s._id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">📅 Den:</span>
          <button onClick={() => shiftDay(-1)} className="px-2 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200" title="Předchozí den">◀</button>
          <input
            type="date"
            value={selectedDay}
            max={localDateStr(new Date())}
            onChange={(e) => setSelectedDay(e.target.value)}
            className="px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:border-purple-500 focus:outline-none"
          />
          <button onClick={() => shiftDay(1)} className="px-2 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200" title="Následující den">▶</button>
          <button onClick={() => setSelectedDay(localDateStr(new Date()))} className="px-3 py-2 rounded-lg bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-200 text-sm font-medium">Dnes</button>
        </div>
        {loading && <span className="text-xs text-gray-400 animate-pulse ml-auto">Načítám…</span>}
      </div>

      {/* Hlavní řádek: pracovní doba/aktivity (70 %) + pravý sloupec (30 %) */}
      <div className="flex flex-col lg:flex-row gap-4 items-start">
        {/* Levý sloupec (70 %) */}
        <div className="w-full lg:w-[70%] bg-white dark:bg-gray-800 rounded-lg shadow p-5">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/2">
              <h3 className="text-base font-bold text-gray-800 dark:text-white mb-1">
                ⏱ Pracovní doba – {userName}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                {new Date(selectedDay).toLocaleDateString('cs-CZ', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
              {workEntry ? (
                <HourlyWorkChart entry={workEntry} onBarClick={openHourList} />
              ) : (
                <div className="text-sm text-gray-400 dark:text-gray-500 py-8 text-center">V tento den nejsou žádné zaznamenané aktivity.</div>
              )}
            </div>
            <div className="w-full md:w-1/2 md:border-l md:border-gray-100 md:dark:border-gray-700 md:pl-6">
              <h3 className="text-base font-bold text-gray-800 dark:text-white mb-1">📈 Vývoj aktivit</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Posledních 14 pracovních dnů</p>
              <ActivityTrendChart points={activityTrend} />
            </div>
          </div>

          {/* Rozpad odpracovaného času po týdnech */}
          <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
            <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">📅 Odpracováno po týdnech (posledních 12)</h4>
            <WorkBreakdownChart bars={weeklyWork.map((w) => ({
              label: w.monday.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' }),
              minutes: w.minutes,
              sub: `${w.days} dní`,
            }))} />
          </div>

          {/* Seznam aktivit za den */}
          <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
            <h3 className="text-base font-bold text-gray-800 dark:text-white mb-3">
              📝 Aktivity {userName} – {new Date(selectedDay).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'long' })} ({dayActivities.length})
            </h3>
            {dayActivities.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-200 dark:border-gray-600 text-left">
                      <th className="py-2 px-2 font-semibold text-gray-700 dark:text-gray-300">Čas</th>
                      <th className="py-2 px-2 font-semibold text-gray-700 dark:text-gray-300">Jméno ZK</th>
                      <th className="py-2 px-2 font-semibold text-gray-700 dark:text-gray-300">IČO</th>
                      <th className="py-2 px-2 font-semibold text-gray-700 dark:text-gray-300">Stav</th>
                      <th className="py-2 px-2 font-semibold text-gray-700 dark:text-gray-300">Aktivita</th>
                      <th className="py-2 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayActivities.map((a, i) => {
                      const stav = companyStatusMap[String(a.companyId)] || a.status;
                      return (
                        <tr
                          key={i}
                          onClick={() => handleOpenCompany(a.companyId)}
                          className="border-b border-gray-100 dark:border-gray-700 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors cursor-pointer"
                          title="Otevřít zákazníka (přepne na účet uživatele)"
                        >
                          <td className="py-2 px-2 text-gray-500 dark:text-gray-400 whitespace-nowrap tabular-nums">{formatTimeIso(a.createdAt)}</td>
                          <td className="py-2 px-2 font-medium text-gray-900 dark:text-white">{a.companyName || '–'}</td>
                          <td className="py-2 px-2 text-gray-600 dark:text-gray-400 tabular-nums">{a.companyIco || '–'}</td>
                          <td className="py-2 px-2">
                            {stav ? (
                              <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">{stav}</span>
                            ) : '–'}
                          </td>
                          <td className="py-2 px-2 text-gray-700 dark:text-gray-300 max-w-md truncate">{a.text || '–'}</td>
                          <td className="py-2 px-2 text-purple-600 dark:text-purple-400 whitespace-nowrap">otevřít →</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-sm text-gray-400 dark:text-gray-500 py-4 text-center">V tento den uživatel nezaznamenal žádnou aktivitu.</div>
            )}
          </div>
        </div>

        {/* Pravý sloupec (30 %): statistické karty + poměry */}
        <div className="w-full lg:w-[30%] flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Uzavřenost leadů" value={`${totals.closeRate} %`} sub={`${totals.closed} / ${totals.generated} uzavřeno`} color="green"
              onClick={closedLeads.length > 0 ? () => openList('Uzavřené leady (forecast 100)', closedLeads, (l) => l.ico ? `IČO ${l.ico}` : '') : undefined} />
            <StatCard label="Reklamovatelnost" value={`${totals.complaintRate} %`} sub={`${totals.complaints} / ${totals.generated} reklamováno`} color="red"
              onClick={complainedLeads.length > 0 ? () => openList('Reklamované leady', complainedLeads, (l) => l.ico ? `IČO ${l.ico}` : '') : undefined} />
            <StatCard label="Vygenerováno leadů" value={totals.generated} sub="Posledních 6 měsíců" color="purple"
              onClick={leads.length > 0 ? () => openList('Vygenerované leady', leads, (l) => `${l.batchName || ''}${l.forecast ? ` · forecast ${parseInt(l.forecast)}%` : ''}`.trim() || (l.ico ? `IČO ${l.ico}` : '')) : undefined} />
            <StatCard label="Aktivit v tento den" value={dayActivities.length} sub={`Odpracováno: ${formatDuration(workEntry?.workedMinutes)}`} color="blue"
              onClick={dayActivities.length > 0 ? () => setListModal({ title: 'Aktivity v tento den', items: dayActivities.map((a) => ({ id: a.companyId ? String(a.companyId) : null, name: a.companyName || '—', sub: `${formatTimeIso(a.createdAt)} · ${a.text || ''}`.trim() })) }) : undefined} />
          </div>

          {/* Uzavřenost leadů po měsících */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">🎯 Uzavřenost leadů (posledních 6 měsíců)</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Zelená = uzavřeno (forecast 100), šedá = vygenerováno</p>
            <LeadsMonthlyChart
              bars={monthly.map((m) => {
                const [y, mo] = m.key.split('-');
                return {
                  label: `${monthShort[parseInt(mo) - 1]} ${String(y).slice(2)}`,
                  generated: m.generated,
                  closed: m.closed,
                  closeRate: m.closeRate,
                };
              })}
              onBarClick={(i) => {
                const m = monthly[i];
                if (!m) return;
                const monthLeads = leads.filter((l) => {
                  const d = new Date(l.createdAt);
                  const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                  return key === m.key;
                });
                openList(`Leady ${m.key} (${m.closed}/${m.generated} uzavřeno)`, monthLeads, (l) => `${l.closed ? '✅ uzavřeno' : `forecast ${parseInt(l.forecast) || 0}%`}${l.ico ? ` · IČO ${l.ico}` : ''}`);
              }}
            />
          </div>

          {/* Poměr PO / FO */}
          <RatioBar
            title="Poměr PO / FO (vygenerované leady)"
            aLabel="Právnická osoba" aCount={poLeads.length} aColor={{ text: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-600' }}
            aOnClick={() => openList('Právnické osoby', poLeads, (l) => l.ico ? `IČO ${l.ico}` : '')}
            bLabel="Fyzická osoba" bCount={foLeads.length} bColor={{ text: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-600' }}
            bOnClick={() => openList('Fyzické osoby', foLeads, (l) => l.ico ? `IČO ${l.ico}` : '')}
          />

          {/* Denní přidělování kontaktů – přepínání měsíců */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h3 className="text-base font-bold text-gray-800 dark:text-white">📥 Přidělené kontakty po dnech</h3>
              <div className="flex items-center gap-2">
                <button onClick={() => shiftAssignMonth(-1)} className="px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200" title="Předchozí měsíc">◀</button>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 min-w-[130px] text-center">{monthNames[assignMonth - 1]} {assignYear}</span>
                <button onClick={() => shiftAssignMonth(1)} className="px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200" title="Následující měsíc">▶</button>
                {assignLoading && <span className="text-xs text-gray-400 animate-pulse">Načítám…</span>}
              </div>
            </div>

            {assignDays && assignDays.length > 0 ? (
              <>
                <div className="mb-3">
                  <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200">
                    Celkem přiděleno: {assignTotal} kontaktů
                  </span>
                </div>
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white dark:bg-gray-800">
                      <tr className="border-b-2 border-gray-200 dark:border-gray-600 text-left">
                        <th className="py-2 px-2 font-semibold text-gray-700 dark:text-gray-300">Den</th>
                        <th className="py-2 px-2 font-semibold text-gray-700 dark:text-gray-300 text-right">Počet</th>
                        <th className="py-2 px-2 font-semibold text-gray-700 dark:text-gray-300">Šarže</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignDays.map((d, i) => (
                        <tr key={i} className="border-b border-gray-100 dark:border-gray-700">
                          <td className="py-2 px-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">{formatDayDate(d.date)}</td>
                          <td className="py-2 px-2 text-right tabular-nums font-semibold text-gray-900 dark:text-white">{d.total}</td>
                          <td className="py-2 px-2 text-gray-500 dark:text-gray-400">
                            <div className="flex flex-wrap gap-1">
                              {(d.batches || []).map((b, j) => (
                                <span key={j} className="inline-block px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                  {b.batchName || 'Bez šarže'}: {b.count}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-400 dark:text-gray-500 py-4 text-center">
                {assignLoading ? 'Načítám…' : 'V tomto měsíci nebyly uživateli přiděleny žádné kontakty.'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modální seznam zákazníků */}
      {listModal && (
        <ListModal
          title={listModal.title}
          items={listModal.items}
          onClose={() => setListModal(null)}
          onOpen={openListItem}
        />
      )}
    </div>
  );
}
